import React, { useState, useEffect, useCallback } from 'react';
import { attendanceAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const fmt = (t) => t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const statusBadge = {
  present:   { bg: '#d1fae5', color: '#065f46' },
  late:      { bg: '#fef3c7', color: '#92400e' },
  'half-day':{ bg: '#fed7aa', color: '#9a3412' },
  absent:    { bg: '#fee2e2', color: '#991b1b' },
  'on-leave':{ bg: '#dbeafe', color: '#1e40af' },
};
const getBadge = (s) => statusBadge[(s || '').toLowerCase()] || { bg: '#f3f4f6', color: '#374151' };

const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const AttendanceManagement = () => {
  const { hasPermission } = useAuth();
  const isPrivileged = hasPermission('employee_view');

  // ── EMPLOYEE: My History ──
  const [myRecords, setMyRecords] = useState([]);
  const [mySummary, setMySummary] = useState(null);
  const [myPage, setMyPage] = useState(1);
  const [myPagination, setMyPagination] = useState({});
  const [myFrom, setMyFrom] = useState(monthStart());
  const [myTo, setMyTo]     = useState(today());
  const [myLoading, setMyLoading] = useState(false);

  // ── ADMIN: All Records ──
  const [allRecords, setAllRecords]   = useState([]);
  const [allLoading, setAllLoading]   = useState(false);
  const [employees, setEmployees]     = useState([]);
  const [filterEmp, setFilterEmp]     = useState('');
  const [filterFrom, setFilterFrom]   = useState(monthStart());
  const [filterTo, setFilterTo]       = useState(today());
  const [filterStatus, setFilterStatus] = useState('');

  // ── Active tab ──
  const [tab, setTab] = useState(isPrivileged ? 'all' : 'my');

  const fetchMyHistory = useCallback(async () => {
    setMyLoading(true);
    try {
      const res = await attendanceAPI.getMy({ page: myPage, limit: 30, from: myFrom, to: myTo });
      setMyRecords(res?.data || []);
      setMySummary(res?.summary || null);
      setMyPagination(res?.pagination || {});
    } catch { setMyRecords([]); }
    finally { setMyLoading(false); }
  }, [myPage, myFrom, myTo]);

  const fetchAll = useCallback(async () => {
    setAllLoading(true);
    try {
      const params = {};
      if (filterEmp)    params.employee   = filterEmp;
      if (filterFrom)   params.startDate  = filterFrom;
      if (filterTo)     params.endDate    = filterTo;
      if (filterStatus) params.status     = filterStatus;
      const res = await attendanceAPI.getAll(params);
      setAllRecords(res?.data || res || []);
    } catch { setAllRecords([]); }
    finally { setAllLoading(false); }
  }, [filterEmp, filterFrom, filterTo, filterStatus]);

  useEffect(() => {
    if (!isPrivileged) fetchMyHistory();
    else {
      fetchAll();
      employeeAPI.getAll().then(r => setEmployees(r?.data || r || [])).catch(() => {});
    }
  }, []);

  const exportCSV = () => {
    const rows = [
      ['Employee', 'Emp ID', 'Date', 'Login Time', 'Logout Time', 'Hours', 'Status', 'Location'],
      ...allRecords.map(r => [
        r.employee?.name || '—',
        r.employee?.employeeId || '—',
        fmtDate(r.date),
        fmt(r.checkIn),
        fmt(r.checkOut),
        r.workHours ? r.workHours + 'h' : '—',
        r.status || '—',
        r.location || '—',
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `attendance_${filterFrom}_${filterTo}.csv`;
    a.click();
  };

  return (
    <div className="att-page">
      {/* ── Tabs ── */}
      <div className="att-header">
        <h2>Attendance</h2>
        <div className="att-tabs">
          {!isPrivileged && (
            <button className={`att-tab ${tab === 'my' ? 'active' : ''}`} onClick={() => setTab('my')}>
              <i className="fas fa-user-clock"></i> My Attendance
            </button>
          )}
          {isPrivileged && (
            <button className={`att-tab ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>
              <i className="fas fa-users"></i> All Records
            </button>
          )}
        </div>
      </div>

      {/* ════════════ EMPLOYEE: MY HISTORY ════════════ */}
      {tab === 'my' && (
        <div>
          {/* Filters */}
          <div className="att-filters">
            <div className="att-filter-grp">
              <label>From</label>
              <input type="date" value={myFrom} onChange={e => setMyFrom(e.target.value)} />
            </div>
            <div className="att-filter-grp">
              <label>To</label>
              <input type="date" value={myTo} onChange={e => setMyTo(e.target.value)} />
            </div>
            <button className="att-filter-btn" onClick={() => { setMyPage(1); fetchMyHistory(); }}>
              <i className="fas fa-search"></i> Filter
            </button>
          </div>

          {/* Summary Cards */}
          {mySummary && (
            <div className="att-cards">
              <div className="att-card">
                <div className="att-card-icon" style={{ background: '#eef2ff', color: '#6366f1' }}>
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div>
                  <div className="att-card-val">{mySummary.totalDays ?? '—'}</div>
                  <div className="att-card-lbl">Total Days</div>
                </div>
              </div>
              <div className="att-card">
                <div className="att-card-icon" style={{ background: '#d1fae5', color: '#059669' }}>
                  <i className="fas fa-user-check"></i>
                </div>
                <div>
                  <div className="att-card-val">{mySummary.presentDays ?? '—'}</div>
                  <div className="att-card-lbl">Present Days</div>
                </div>
              </div>
              <div className="att-card">
                <div className="att-card-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                  <i className="fas fa-clock"></i>
                </div>
                <div>
                  <div className="att-card-val">{mySummary.totalHours != null ? mySummary.totalHours + 'h' : '—'}</div>
                  <div className="att-card-lbl">Total Hours</div>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          {myLoading ? (
            <Spinner />
          ) : (
            <>
              <div className="att-table-wrap">
                <table className="att-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Login Time</th>
                      <th>Logout Time</th>
                      <th>Total Hours</th>
                      <th>Status</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myRecords.length === 0
                      ? <tr><td colSpan="6" className="att-empty">No records found for selected period</td></tr>
                      : myRecords.map(r => {
                        const badge = getBadge(r.status);
                        return (
                          <tr key={r._id}>
                            <td>{fmtDate(r.date)}</td>
                            <td><span className="att-time-chip">{fmt(r.checkIn)}</span></td>
                            <td><span className="att-time-chip">{fmt(r.checkOut)}</span></td>
                            <td>{r.workHours != null ? <strong>{r.workHours}h</strong> : '—'}</td>
                            <td>
                              <span className="att-badge" style={{ background: badge.bg, color: badge.color }}>
                                {r.status || '—'}
                              </span>
                            </td>
                            <td>{r.location || (r.isRemote ? '🏠 Remote' : '🏢 Office')}</td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {myPagination.pages > 1 && (
                <div className="att-pagination">
                  <button disabled={myPage <= 1} onClick={() => { setMyPage(p => p - 1); fetchMyHistory(); }}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span>Page {myPage} / {myPagination.pages}</span>
                  <button disabled={myPage >= myPagination.pages} onClick={() => { setMyPage(p => p + 1); fetchMyHistory(); }}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ════════════ ADMIN: ALL RECORDS ════════════ */}
      {tab === 'all' && (
        <div>
          {/* Filters */}
          <div className="att-filters">
            <div className="att-filter-grp">
              <label>Employee</label>
              <select value={filterEmp} onChange={e => setFilterEmp(e.target.value)}>
                <option value="">All Employees</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>
                ))}
              </select>
            </div>
            <div className="att-filter-grp">
              <label>From</label>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </div>
            <div className="att-filter-grp">
              <label>To</label>
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </div>
            <div className="att-filter-grp">
              <label>Status</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="late">Late</option>
                <option value="half-day">Half Day</option>
                <option value="on-leave">On Leave</option>
              </select>
            </div>
            <button className="att-filter-btn" onClick={fetchAll}>
              <i className="fas fa-search"></i> Filter
            </button>
            <button className="att-export-btn" onClick={exportCSV} title="Export to CSV">
              <i className="fas fa-file-csv"></i> Export
            </button>
          </div>

          {/* Table */}
          {allLoading ? (
            <Spinner />
          ) : (
            <div className="att-table-wrap">
              <div className="att-records-count">{allRecords.length} record(s) found</div>
              <table className="att-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Emp ID</th>
                    <th>Date</th>
                    <th>Login Time</th>
                    <th>Logout Time</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {allRecords.length === 0
                    ? <tr><td colSpan="8" className="att-empty">No records found</td></tr>
                    : allRecords.map(r => {
                      const badge = getBadge(r.status);
                      return (
                        <tr key={r._id}>
                          <td><strong>{r.employee?.name || '—'}</strong></td>
                          <td>{r.employee?.employeeId || '—'}</td>
                          <td>{fmtDate(r.date)}</td>
                          <td><span className="att-time-chip">{fmt(r.checkIn)}</span></td>
                          <td><span className="att-time-chip">{fmt(r.checkOut)}</span></td>
                          <td>{r.workHours != null ? <strong>{r.workHours}h</strong> : '—'}</td>
                          <td>
                            <span className="att-badge" style={{ background: badge.bg, color: badge.color }}>
                              {r.status || '—'}
                            </span>
                          </td>
                          <td>{r.location || (r.isRemote ? '🏠 Remote' : '🏢 Office')}</td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <style>{`
        .att-page { padding: 0; }
        .att-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
        .att-header h2 { font-size: 22px; font-weight: 700; color: #111827; margin: 0; }
        .att-tabs { display: flex; gap: 8px; }
        .att-tab { padding: 8px 20px; border: 2px solid #e5e7eb; background: #fff; border-radius: 8px; font-size: 14px; font-weight: 600; color: #6b7280; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 7px; }
        .att-tab.active { background: #6366f1; color: #fff; border-color: #6366f1; }
        .att-tab:hover:not(.active) { background: #f3f4f6; }

        .att-filters { background: #fff; border-radius: 12px; padding: 18px 20px; display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; margin-bottom: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
        .att-filter-grp { display: flex; flex-direction: column; gap: 5px; }
        .att-filter-grp label { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.4px; }
        .att-filter-grp input, .att-filter-grp select { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; background: #fff; color: #111; min-width: 140px; }
        .att-filter-grp input:focus, .att-filter-grp select:focus { outline: none; border-color: #6366f1; }
        .att-filter-btn { padding: 8px 18px; background: #6366f1; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; height: 36px; align-self: flex-end; }
        .att-filter-btn:hover { background: #4f46e5; }
        .att-export-btn { padding: 8px 16px; background: #10b981; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; height: 36px; align-self: flex-end; }
        .att-export-btn:hover { background: #059669; }

        .att-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
        .att-card { background: #fff; border-radius: 12px; padding: 18px 20px; display: flex; align-items: center; gap: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
        .att-card-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
        .att-card-val { font-size: 26px; font-weight: 700; color: #111827; line-height: 1; }
        .att-card-lbl { font-size: 12px; color: #6b7280; margin-top: 4px; font-weight: 500; }

        .att-records-count { font-size: 13px; color: #6b7280; margin-bottom: 10px; }
        .att-table-wrap { background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
        .att-table { width: 100%; border-collapse: collapse; }
        .att-table th { background: #f8fafc; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e5e7eb; }
        .att-table td { padding: 13px 16px; font-size: 14px; color: #111827; border-bottom: 1px solid #f1f5f9; }
        .att-table tr:last-child td { border-bottom: none; }
        .att-table tr:hover td { background: #fafbff; }
        .att-empty { text-align: center; color: #9ca3af; padding: 40px !important; font-size: 15px; }
        .att-badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; text-transform: capitalize; }
        .att-time-chip { font-size: 13px; font-weight: 600; color: #374151; background: #f3f4f6; padding: 3px 9px; border-radius: 6px; }

        .att-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; padding: 20px; }
        .att-pagination button { padding: 7px 14px; border: 1px solid #d1d5db; background: #fff; border-radius: 6px; cursor: pointer; font-size: 13px; }
        .att-pagination button:disabled { opacity: 0.4; cursor: not-allowed; }
        .att-pagination span { font-size: 14px; color: #374151; font-weight: 500; }

        @media (max-width: 700px) {
          .att-cards { grid-template-columns: 1fr; }
          .att-table-wrap { overflow-x: auto; }
          .att-table { min-width: 600px; }
        }
      `}</style>
    </div>
  );
};

const Spinner = () => (
  <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
    <i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i>
    <p style={{ marginTop: 12 }}>Loading...</p>
  </div>
);

export default AttendanceManagement;

